/**
 * PublicHolidaysPage (v37 — May 21, 2026)
 *
 * Yearly public-holiday calendar. HR/Admin can publish (CRUD + bulk template).
 * Everyone else gets read-only view filtered by year. Integrates with attendance
 * automatically — when a staff's check-in date matches a published holiday, the
 * attendance row is auto-tagged status='holiday' in the seeder/mark-in handlers
 * (see /api/attendance/mark-in).
 */
import { useEffect, useState, useMemo } from 'react';
import { v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon, Plus, Pencil, Trash2, Upload, Download, Loader2, ChevronLeft, ChevronRight, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { cn } from '../lib/utils';

const HOLIDAY_TYPES = [
  { value: 'national', label: 'National', color: '#ef4444' },
  { value: 'regional', label: 'Regional', color: '#f59e0b' },
  { value: 'religious', label: 'Religious', color: '#8b5cf6' },
  { value: 'hospital', label: 'Hospital-specific', color: '#3b82f6' },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PublicHolidaysPage = () => {
  const { user } = useAuth();
  const isHR = user?.is_super_admin || ['hospital_admin', 'hr'].includes(user?.role_code) || ['Hospital Admin', 'HR'].includes(user?.role_name);

  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', holiday_type: 'national', description: '', is_optional: false, color: '#ef4444' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await v37API.listHolidays(year);
      setHolidays(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', date: `${year}-01-01`, holiday_type: 'national', description: '', is_optional: false, color: '#ef4444' });
    setIsOpen(true);
  };
  const openEdit = (h) => {
    setEditId(h.id);
    setForm({
      name: h.name, date: h.date,
      holiday_type: h.holiday_type || 'national',
      description: h.description || '',
      is_optional: !!h.is_optional,
      color: h.color || '#ef4444',
    });
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editId) await v37API.updateHoliday(editId, form);
      else await v37API.createHoliday(form);
      toast.success(editId ? 'Holiday updated' : 'Holiday published');
      setIsOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.name}" on ${h.date}?`)) return;
    try {
      await v37API.deleteHoliday(h.id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Seed India national/regional template
  const handleSeedIndia = async () => {
    if (!window.confirm(`Publish 12 standard India public holidays for ${year}? Existing ones with same name+date will be skipped.`)) return;
    const seed = [
      { name: "New Year's Day", date: `${year}-01-01`, holiday_type: 'national', color: '#3b82f6' },
      { name: 'Republic Day', date: `${year}-01-26`, holiday_type: 'national', color: '#ef4444' },
      { name: 'Holi', date: `${year}-03-14`, holiday_type: 'religious', color: '#ec4899' },
      { name: 'Good Friday', date: `${year}-04-03`, holiday_type: 'religious', color: '#8b5cf6' },
      { name: 'Eid al-Fitr', date: `${year}-04-21`, holiday_type: 'religious', color: '#10b981' },
      { name: 'Independence Day', date: `${year}-08-15`, holiday_type: 'national', color: '#f97316' },
      { name: 'Janmashtami', date: `${year}-08-26`, holiday_type: 'religious', color: '#06b6d4' },
      { name: 'Gandhi Jayanti', date: `${year}-10-02`, holiday_type: 'national', color: '#ef4444' },
      { name: 'Dussehra', date: `${year}-10-12`, holiday_type: 'religious', color: '#f59e0b' },
      { name: 'Diwali', date: `${year}-11-01`, holiday_type: 'religious', color: '#facc15' },
      { name: 'Guru Nanak Jayanti', date: `${year}-11-15`, holiday_type: 'religious', color: '#8b5cf6' },
      { name: 'Christmas Day', date: `${year}-12-25`, holiday_type: 'religious', color: '#dc2626' },
    ];
    try {
      const res = await v37API.bulkHolidays(seed);
      toast.success(`Inserted ${res.data.inserted}, skipped ${res.data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Group holidays by month for the month-grid view
  const byMonth = useMemo(() => {
    const m = Array.from({ length: 12 }, () => []);
    holidays.forEach(h => {
      const month = parseInt((h.date || '').slice(5, 7), 10) - 1;
      if (month >= 0 && month < 12) m[month].push(h);
    });
    return m;
  }, [holidays]);

  return (
    <div className="space-y-5" data-testid="public-holidays-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Public Holidays</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {isHR ? 'Publish and manage the yearly holiday calendar — visible to all staff.' : 'Annual list of public holidays at your hospital.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(y => y - 1)} className="h-9 w-9 rounded-md border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="holidays-prev-year">
            <ChevronLeft size={14} />
          </button>
          <div className="font-bold text-slate-900 dark:text-slate-100 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-md text-sm" data-testid="holidays-current-year">{year}</div>
          <button onClick={() => setYear(y => y + 1)} className="h-9 w-9 rounded-md border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="holidays-next-year">
            <ChevronRight size={14} />
          </button>
          {isHR && (
            <>
              <Button variant="outline" size="sm" onClick={handleSeedIndia} className="h-9 gap-1.5" data-testid="holidays-seed-india">
                <Upload size={12} /> Seed India ({year})
              </Button>
              <Button size="sm" onClick={openCreate} className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="holidays-add-btn">
                <Plus size={12} /> Add Holiday
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 gap-2"><Loader2 className="animate-spin" size={20} /> Loading…</div>
      ) : (
        <>
          {/* Month grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {MONTHS.map((m, idx) => {
              const items = byMonth[idx];
              return (
                <div key={m} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-3" data-testid={`holiday-month-${m}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200">{m} {year}</h3>
                    <span className="text-[10px] text-slate-400">{items.length} holiday{items.length === 1 ? '' : 's'}</span>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-[11px] text-slate-300 italic py-3 text-center">No holidays</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {items.map(h => (
                        <li key={h.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`holiday-row-${h.id}`}>
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: h.color || '#ef4444' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">{h.name}</p>
                            <p className="text-[10px] text-slate-500">{h.date.slice(8, 10)} {m} · {h.holiday_type}{h.is_optional ? ' · optional' : ''}</p>
                          </div>
                          {isHR && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                              <button onClick={() => openEdit(h)} className="h-6 w-6 rounded text-blue-600 hover:bg-blue-50" title="Edit" data-testid={`holiday-edit-${h.id}`}><Pencil size={11} /></button>
                              <button onClick={() => handleDelete(h)} className="h-6 w-6 rounded text-red-600 hover:bg-red-50" title="Delete" data-testid={`holiday-delete-${h.id}`}><Trash2 size={11} /></button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {holidays.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="holidays-empty">
              <CalendarIcon size={28} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No holidays published for {year} yet.</p>
              {isHR && <p className="text-xs text-slate-400 mt-1">Click <b>Add Holiday</b> or <b>Seed India ({year})</b> above to get started.</p>}
            </div>
          )}
        </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Holiday' : 'Publish Holiday'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Republic Day" required data-testid="holiday-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="holiday-date-input" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.holiday_type} onValueChange={v => {
                  const t = HOLIDAY_TYPES.find(x => x.value === v);
                  setForm(f => ({ ...f, holiday_type: v, color: t?.color || f.color }));
                }}>
                  <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOLIDAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description (optional)</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief note" data-testid="holiday-description" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_optional} onCheckedChange={v => setForm(f => ({ ...f, is_optional: v }))} data-testid="holiday-optional-switch" /><Label className="text-xs">Optional holiday (staff may choose to work)</Label></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="holiday-submit-btn">
                {submitting ? 'Saving…' : (editId ? 'Save Changes' : 'Publish')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicHolidaysPage;
